import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  useStripe,
  useElements,
  PaymentElement,
  AddressElement
} from '@stripe/react-stripe-js'
import { useCurrency } from '../contexts/CurrencyContext'
import {
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

interface CheckoutFormProps {
  onSuccess: (paymentIntentId: string) => void
  planName: string
  amount: number
}

const CheckoutForm = ({ onSuccess, planName, amount }: CheckoutFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const { formatPriceInCurrency, selectedCurrency } = useCurrency()
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setLoading(true)
    setError(null)
    setProcessing(true)

    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        setError(submitError.message || 'Payment submission failed')
        return
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: 'if_required'
      })

      if (confirmError) {
        setError(confirmError.message || 'Payment confirmation failed')
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id)
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
      console.error('Payment error:', err)
    } finally {
      setLoading(false)
      setProcessing(false)
    }
  }

  const paymentElementOptions = {
    layout: 'tabs' as const,
    defaultValues: {
      billingDetails: {
        email: '',
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <CreditCardIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-medium">{planName} Plan</p>
              <p className="text-gray-400 text-sm">Monthly subscription</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-white font-bold text-lg">{formatPriceInCurrency(amount / 100, selectedCurrency)}</p>
            <p className="text-gray-400 text-sm">per month</p>
          </div>
        </div>
      </div>

      {/* Payment Element */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Payment Information
          </label>
          <div className="payment-element-container">
            <PaymentElement 
              options={paymentElementOptions}
              className="payment-element"
            />
          </div>
        </div>

        {/* Billing Address */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Billing Address
          </label>
          <div className="address-element-container">
            <AddressElement 
              options={{
                mode: 'billing',
                allowedCountries: ['US', 'CA', 'GB', 'AU'],
                defaultValues: {
                  address: {
                    country: 'US'
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 rounded-xl p-4"
        >
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Processing Status */}
      {processing && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4"
        >
          <div className="flex items-center space-x-3">
            <div className="animate-spin w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full"></div>
            <p className="text-blue-400 text-sm">Processing your payment...</p>
          </div>
        </motion.div>
      )}

      {/* Submit Button */}
      <motion.button
        type="submit"
        disabled={!stripe || loading || processing}
        className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all ${
          !stripe || loading || processing
            ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white'
        }`}
        whileHover={{ scale: (!stripe || loading || processing) ? 1 : 1.02 }}
        whileTap={{ scale: (!stripe || loading || processing) ? 1 : 0.98 }}
      >
        {loading || processing ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
            <span>Processing Payment...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-2">
            <CheckCircleIcon className="w-5 h-5" />
            <span>Complete Payment • {formatPriceInCurrency(amount / 100, selectedCurrency)}</span>
          </div>
        )}
      </motion.button>

      {/* Security Notice */}
      <div className="text-center">
        <p className="text-gray-400 text-xs">
          Your payment is secured by Stripe. We never store your card details.
        </p>
      </div>
    </form>
  )
}

export default CheckoutForm 